import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { runServerFn } from "@/lib/server-fn";
import {
  listSlottingRecommendations,
  generateSlottingRecommendations,
  updateSlottingRecommendationStatus,
} from "@/lib/slotting.functions";
import { SlottingRunProvenance } from "@/components/slotting/slotting-run-provenance";

export const Route = createFileRoute("/_authenticated/app/admin/slotting")({ component: SlottingPage });

function SlottingPage() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const list = useServerFn(listSlottingRecommendations);
  const gen = useServerFn(generateSlottingRecommendations);
  const setStatus = useServerFn(updateSlottingRecommendationStatus);
  const [horizon, setHorizon] = useState("30");
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["slotting-recs", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => list({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const generate = async () => {
    if (!tenantId || !facilityId) return toast.error("Select a facility from the scope bar first");
    setBusy(true);
    try {
      const res = (await runServerFn(
        () => gen({ data: { tenantId, facilityId, horizonDays: parseInt(horizon, 10) || 30 } }),
        "Generate recommendations",
      )) as any;
      toast.success(`AI generated ${res.recommendations?.length ?? 0} recommendations`);
      qc.invalidateQueries({ queryKey: ["slotting-recs"] });
    } catch { /* toasted */ }
    finally { setBusy(false); }
  };

  const updateStatus = async (id: string, status: "reviewed" | "applied" | "dismissed") => {
    if (!tenantId) return;
    await runServerFn(() => setStatus({ data: { id, tenantId, status } }), `Mark ${status}`);
    qc.invalidateQueries({ queryKey: ["slotting-recs"] });
  };

  return (
    <>
      <PageHeader
        title="AI Slotting & Replenishment"
        description="Lovable AI analyzes pick velocity, SKU weight, and your location grid to recommend slotting moves and replenishment rule tweaks. Review, apply, or dismiss each run."
        actions={
          <div className="flex items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">Horizon (days)</Label>
              <Input type="number" className="w-24" value={horizon} onChange={(e) => setHorizon(e.target.value)} /></div>
            <Button onClick={generate} disabled={busy || !facilityId}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        {(data?.runs ?? []).length === 0 ? (
          <Card><CardContent className="p-6 text-sm text-center text-muted-foreground">No recommendation runs yet. Click Generate to ask the AI for a slotting plan.</CardContent></Card>
        ) : (
          (data?.runs ?? []).map((run: any) => (
            <Card key={run.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base">{new Date(run.generated_at).toLocaleString()}</CardTitle>
                    <div className="text-xs text-muted-foreground mt-1">Model {run.model} · {run.horizon_days}d horizon · {(run.recommendations ?? []).length} items</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={run.status === "applied" ? "default" : "outline"}>{run.status}</Badge>
                    {run.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(run.id, "reviewed")}>Reviewed</Button>
                        <Button size="sm" onClick={() => updateStatus(run.id, "applied")}>Applied</Button>
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(run.id, "dismissed")}>Dismiss</Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <SlottingRunProvenance run={run} />
                {run.summary && <p className="text-sm text-muted-foreground">{run.summary}</p>}
                <ul className="space-y-2">
                  {(run.recommendations ?? []).map((r: any, i: number) => (
                    <li key={i} className="rounded border p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline">{r.type}</Badge>
                        <Badge variant={r.priority === "high" ? "destructive" : r.priority === "medium" ? "default" : "secondary"}>{r.priority}</Badge>
                        {r.sku_code && <span className="font-mono text-xs">{r.sku_code}</span>}
                        {r.from_location && <span className="text-xs text-muted-foreground">{r.from_location} → {r.to_location}</span>}
                        {r.forward_location && <span className="text-xs text-muted-foreground">fwd {r.forward_location} ({r.min_qty}-{r.max_qty})</span>}
                        {r.validation_warnings && <Badge variant="destructive" className="text-xs">{r.validation_warnings.join(", ")}</Badge>}
                      </div>
                      <p>{r.reason}</p>
                      {r.estimated_impact && <p className="text-xs text-muted-foreground mt-1">Impact: {r.estimated_impact}</p>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </>
  );
}